import React, { useCallback, useImperativeHandle, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { SectionListRenderItemInfo } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Button } from '@/components/ButtonComponent';
import { SocialCurrencyActiveIcon } from '@/components/SocialCurrencyActiveIcon';
import { Text } from '@/components/TextComponent';
import { Box, Row } from '@/components/lib/components';
import { Modal } from '@/feature/alerts/components/Modal';
import { BetslipModalHeader } from '@/feature/betslip-sbk/components/BetslipModalHeader';
import { EntrySummaryItemParlay } from '@/feature/betslip-sbk/components/entry-summary-modal/EntrySummaryItemParlay';
import { EntrySummaryItemSingle } from '@/feature/betslip-sbk/components/entry-summary-modal/EntrySummaryItemSingle';
import { betslipModalStyles } from '@/feature/betslip-sbk/components/shared-modal-styles';
import { useColumnWidths } from '@/feature/betslip-sbk/hooks/use-column-widths';
import { useSbkBetSlipStore } from '@/feature/betslip-sbk/hooks/use-sbk-betslip-store';
import { BetTypes } from '@/feature/betslip-sbk/types';
import { totalPayoutSelector } from '@/feature/betslip-sbk/utils/betslip-selectors';
import { isBetValid } from '@/feature/betslip-sbk/utils/betslip-utils';
import { useFormatCurrency } from '@/hooks/use-format-currency';
import { useIsCashCurrency } from '@/hooks/use-social-currency';
import { showScrollIndicator } from '@/utils/constants-platform-specific';
import {
    BottomSheetFooter,
    BottomSheetFooterProps,
    BottomSheetModal,
    BottomSheetSectionList,
} from '@gorhom/bottom-sheet';
import { useShallow } from 'zustand/react/shallow';

import { BetSlipModalStickyHeaderRow } from '../BetSlipModalStickyHeaderRow';
import { EntrySummaryWarnings } from './EntrySummaryWarnings/EntrySummaryWarnings';

export type SbkEntrySummaryModalRef = {
    show: () => void;
    dismiss: () => void;
};

const MODAL_ID = 'sbkEntrySummaryModal';

type SectionData = {
    title: string;
    data: string[];
};

const DEFAULT_COLUMN_WIDTH = 52;

const keyExtractor = (item: string) => item;

export const SbkEntrySummaryModal = React.forwardRef<SbkEntrySummaryModalRef, object>((_props, ref) => {
    const modalRef = useRef<BottomSheetModal>(null);
    const { t } = useTranslation(['betslip_sbk', 'common']);
    const { formatCurrency } = useFormatCurrency();
    const { bottom } = useSafeAreaInsets();

    const isCashCurrency = useIsCashCurrency();
    const totalPayout = useSbkBetSlipStore(totalPayoutSelector(isCashCurrency));

    const { columnWidths, createLayoutHandler } = useColumnWidths({
        entry: DEFAULT_COLUMN_WIDTH,
        payout: DEFAULT_COLUMN_WIDTH,
    });

    // Get list of valid bet IDs (bets with stake), ordered: parlay first, then singles
    const validBetIds = useSbkBetSlipStore(
        useShallow(state => {
            const betsWithStake = Object.values(state.bets).filter(
                bet => !!bet.stake && (bet.betType === BetTypes.Single || isBetValid(bet, state))
            );
            return betsWithStake
                .sort((a, b) => (a.betType === BetTypes.Combo ? -1 : b.betType === BetTypes.Combo ? 1 : 0))
                .map(bet => bet.id);
        })
    );

    const bets = useSbkBetSlipStore(state => state.bets);

    useImperativeHandle(ref, () => ({
        show: () => {
            modalRef.current?.present();
        },
        dismiss: () => {
            modalRef.current?.dismiss();
        },
    }));

    const handleClose = useCallback(() => {
        modalRef.current?.dismiss();
    }, []);

    // Create section data with a single section containing all bet IDs
    const sections: SectionData[] = useMemo(
        () => [
            {
                title: 'entries',
                data: validBetIds,
            },
        ],
        [validBetIds]
    );

    const renderSectionHeader = useCallback(
        () => (
            <BetSlipModalStickyHeaderRow>
                <Box flex={1}>
                    <Text variant="bodySmall" color="neutral300">
                        {t('picks_header')}
                    </Text>
                </Box>
                <Box minWidth={columnWidths.entry} pr="s16">
                    <Text variant="bodySmall" color="neutral300" textAlign="right">
                        {t('entry_header')}
                    </Text>
                </Box>
                <Box minWidth={columnWidths.payout}>
                    <Text variant="bodySmall" color="neutral300" textAlign="right">
                        {t('payout_header')}
                    </Text>
                </Box>
            </BetSlipModalStickyHeaderRow>
        ),
        [t, columnWidths.entry, columnWidths.payout]
    );

    const renderItem = useCallback(
        ({ item: betId }: SectionListRenderItemInfo<string>) => {
            const bet = bets[betId];
            return bet?.betType === BetTypes.Combo ? (
                <EntrySummaryItemParlay
                    betId={betId}
                    columnWidths={columnWidths}
                    createLayoutHandler={createLayoutHandler}
                />
            ) : (
                <EntrySummaryItemSingle
                    betId={betId}
                    columnWidths={columnWidths}
                    createLayoutHandler={createLayoutHandler}
                />
            );
        },
        [bets, columnWidths, createLayoutHandler]
    );

    const renderListHeader = useCallback(
        () => (
            <>
                <BetslipModalHeader title={t('entry_summary')}>
                    <Row alignItems="center" justifyContent="center" gap="s4">
                        <Text variant="bodyMedium" color="neutral300">
                            {t('total_payout')}:
                        </Text>
                        <SocialCurrencyActiveIcon />
                        <Text variant="bodyMedium" color="neutral300">
                            {formatCurrency(totalPayout)}
                        </Text>
                    </Row>
                </BetslipModalHeader>
                <EntrySummaryWarnings />
            </>
        ),
        [t, formatCurrency, totalPayout]
    );

    const footerStyle = useMemo(() => ({ paddingBottom: bottom + 16 || 16 }), [bottom]);

    const footerComponent = useCallback(
        (props: BottomSheetFooterProps) => (
            <BottomSheetFooter {...props} style={betslipModalStyles.contentContainer}>
                <Box px="s16" pt="s16" style={footerStyle}>
                    <Button label={t('common:close')} hierarchy="primary" onPress={handleClose} />
                </Box>
            </BottomSheetFooter>
        ),
        [t, handleClose, footerStyle]
    );

    return (
        <Modal id={MODAL_ID} modalRef={modalRef} hasBottomSheetView={false} footerComponent={footerComponent}>
            <BottomSheetSectionList
                sections={sections}
                keyExtractor={keyExtractor}
                renderItem={renderItem}
                renderSectionHeader={renderSectionHeader}
                ListHeaderComponent={renderListHeader}
                stickySectionHeadersEnabled={true}
                contentContainerStyle={betslipModalStyles.listContentLargerPadding}
                showsVerticalScrollIndicator={showScrollIndicator}
                enableFooterMarginAdjustment={true}
            />
        </Modal>
    );
});
